import { apiClient } from "@/api/api-client";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/core/actions/dropdown-menu";
import { Button } from "@/components/core/buttons/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/core/card";
import { Input } from "@/components/core/forms/input";
import { useInvoiceCollectionReportQuery } from "@/features/dashboard/api/queries/contract-invoice-query";
import { useAuth } from "@/hooks/use-auth";
import { formatNumber } from "@/utils/format-number";
import {
	IconCalendar,
	IconCheck,
	IconChevronDown,
	IconChevronUp,
	IconFileExport,
	IconFileSpreadsheet,
	IconFileTypeCsv,
	IconFileTypePdf,
	IconSearch,
	IconX,
} from "@tabler/icons-react";
import { endOfYear, format, startOfYear } from "date-fns";
import type React from "react";
import { useMemo, useState } from "react";

interface InvoiceReportData {
	id: string;
	client_name: string;
	project_name: string;
	salesperson_name: string;
	contract_amount: number;
	monthly_recurring_sales: number;
	invoices: {
		id: string;
		invoice_number: string;
		amount: number;
		invoice_date: string;
		due_date: string;
		status: "pending" | "paid" | "overdue" | "cancelled";
		collection_status: "pending" | "collected";
		description?: string;
	}[];
}

export const CollectionReport: React.FC = () => {
	const { user, hasRole } = useAuth();

	// Year filter state
	const currentYear = new Date().getFullYear();
	const [selectedYear, setSelectedYear] = useState(currentYear);

	// Search state
	const [searchTerm, setSearchTerm] = useState("");

	// Breakdown state
	const [showCollectedBreakdown, setShowCollectedBreakdown] = useState(false);
	const [showPendingBreakdown, setShowPendingBreakdown] = useState(false);

	// For salesperson users, filter to only their own contracts
	const salespersonId = hasRole(["salesperson"]) && !hasRole(["admin"]) ? user?.id : undefined;

	const { data: rawData, isLoading, error } = useInvoiceCollectionReportQuery(selectedYear, salespersonId);

	// Filter data by search term
	const filteredData = useMemo(() => {
		if (!rawData) return [];

		// Filter by search term (similar to sales contract list)
		return rawData.filter((contract) => {
			if (!searchTerm.trim()) return true;

			const searchLower = searchTerm.toLowerCase();
			return (
				contract.client_name?.toLowerCase().includes(searchLower) ||
				contract.project_name?.toLowerCase().includes(searchLower) ||
				contract.salesperson_name?.toLowerCase().includes(searchLower)
			);
		});
	}, [rawData, searchTerm]);

	// Calculate group totals
	const groupTotals = useMemo(() => {
		if (!filteredData || filteredData.length === 0) {
			return {
				recurring: { contractAmount: 0, monthlyRecurring: 0, collected: 0, pending: 0, count: 0 },
				oneTime: { contractAmount: 0, collected: 0, pending: 0, count: 0 },
			};
		}

		const recurringContracts = filteredData.filter((contract) => contract.monthly_recurring_sales > 0);
		const oneTimeContracts = filteredData.filter((contract) => contract.monthly_recurring_sales === 0);

		const recurringTotals = {
			contractAmount: recurringContracts.reduce((sum, contract) => sum + contract.contract_amount, 0),
			monthlyRecurring: recurringContracts.reduce((sum, contract) => sum + contract.monthly_recurring_sales, 0),
			collected: 0,
			pending: 0,
			count: recurringContracts.length,
		};

		const oneTimeTotals = {
			contractAmount: oneTimeContracts.reduce((sum, contract) => sum + contract.contract_amount, 0),
			collected: 0,
			pending: 0,
			count: oneTimeContracts.length,
		};

		// Calculate collected and pending amounts for each group
		for (const contract of recurringContracts) {
			const collectedInvoices = contract.invoices.filter((invoice) => invoice.collection_status === "collected");
			const pendingInvoices = contract.invoices.filter((invoice) => invoice.collection_status === "pending");
			recurringTotals.collected += collectedInvoices.reduce((sum, invoice) => sum + invoice.amount, 0);
			recurringTotals.pending += pendingInvoices.reduce((sum, invoice) => sum + invoice.amount, 0);
		}

		for (const contract of oneTimeContracts) {
			const collectedInvoices = contract.invoices.filter((invoice) => invoice.collection_status === "collected");
			const pendingInvoices = contract.invoices.filter((invoice) => invoice.collection_status === "pending");
			oneTimeTotals.collected += collectedInvoices.reduce((sum, invoice) => sum + invoice.amount, 0);
			oneTimeTotals.pending += pendingInvoices.reduce((sum, invoice) => sum + invoice.amount, 0);
		}

		return {
			recurring: recurringTotals,
			oneTime: oneTimeTotals,
		};
	}, [filteredData]);

	// Calculate summary from the filtered data (search-aware)
	const summaryData = useMemo(() => {
		if (!filteredData || filteredData.length === 0) {
			return {
				totalCollected: 0,
				pendingCollection: 0,
				collectedBreakdown: { oneTime: 0, recurring: 0, oneTimeProjects: [], recurringProjects: [] },
				pendingBreakdown: { oneTime: 0, recurring: 0, oneTimeProjects: [], recurringProjects: [] },
			};
		}

		// Calculate collected and pending amounts based on invoice collection status
		let collectedAmount = 0;
		let pendingAmount = 0;
		const collectedBreakdown = { oneTime: 0, recurring: 0, oneTimeProjects: [], recurringProjects: [] };
		const pendingBreakdown = { oneTime: 0, recurring: 0, oneTimeProjects: [], recurringProjects: [] };

		for (const contract of filteredData) {
			const collectedInvoices = contract.invoices.filter((invoice) => invoice.collection_status === "collected");
			const pendingInvoices = contract.invoices.filter((invoice) => invoice.collection_status === "pending");

			// Check if this is a one-time project
			const isOneTime = contract.monthly_recurring_sales === 0;

			if (collectedInvoices.length > 0) {
				const collectedTotal = collectedInvoices.reduce((sum, invoice) => sum + invoice.amount, 0);
				collectedAmount += collectedTotal;

				if (isOneTime) {
					collectedBreakdown.oneTime += collectedTotal;
					collectedBreakdown.oneTimeProjects.push({
						project: contract.project_name,
						client: contract.client_name,
						amount: collectedTotal,
					});
				} else {
					collectedBreakdown.recurring += collectedTotal;
					collectedBreakdown.recurringProjects.push({
						project: contract.project_name,
						client: contract.client_name,
						monthlyAmount: contract.monthly_recurring_sales,
						months: collectedInvoices.length,
						totalAmount: collectedTotal,
					});
				}
			}

			if (pendingInvoices.length > 0) {
				const pendingTotal = pendingInvoices.reduce((sum, invoice) => sum + invoice.amount, 0);
				pendingAmount += pendingTotal;

				if (isOneTime) {
					pendingBreakdown.oneTime += pendingTotal;
					pendingBreakdown.oneTimeProjects.push({
						project: contract.project_name,
						client: contract.client_name,
						amount: pendingTotal,
					});
				} else {
					pendingBreakdown.recurring += pendingTotal;
					pendingBreakdown.recurringProjects.push({
						project: contract.project_name,
						client: contract.client_name,
						monthlyAmount: contract.monthly_recurring_sales,
						months: pendingInvoices.length,
						totalAmount: pendingTotal,
					});
				}
			}
		}

		return {
			totalCollected: collectedAmount,
			pendingCollection: pendingAmount,
			collectedBreakdown,
			pendingBreakdown,
		};
	}, [filteredData]);

	const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

	const getMonthlyStatus = (contract: InvoiceReportData) => {
		const monthlyStatus: { [key: string]: "collected" | "partial" | "pending" | "not_applicable" } = {};

		// Initialize all months as not_applicable
		for (let i = 1; i <= 12; i++) {
			const monthKey = i.toString().padStart(2, "0");
			monthlyStatus[monthKey] = "not_applicable";
		}

		// Process invoices to determine monthly status based on invoice dates
		for (const invoice of contract.invoices) {
			const invoiceMonth = new Date(invoice.due_date).getMonth() + 1;
			const monthKey = invoiceMonth.toString().padStart(2, "0");

			// If there's any invoice for this month, determine status
			if (invoice.collection_status === "collected") {
				monthlyStatus[monthKey] = "collected";
			} else if (invoice.collection_status === "partial") {
				if (monthlyStatus[monthKey] !== "collected") {
					monthlyStatus[monthKey] = "partial";
				}
			} else if (monthlyStatus[monthKey] !== "collected" && monthlyStatus[monthKey] !== "partial") {
				monthlyStatus[monthKey] = "pending";
			}
		}

		return monthlyStatus;
	};

	const getStatusIcon = (status: "collected" | "partial" | "pending" | "not_applicable") => {
		switch (status) {
			case "collected":
				return <IconCheck className="w-4 h-4 text-green-600" />;
			case "partial":
				return <IconCheck className="w-4 h-4 text-blue-600" />;
			case "pending":
				return <IconX className="w-4 h-4 text-red-600" />;
			case "not_applicable":
				return <span className="w-4 h-4 text-gray-400">-</span>;
			default:
				return <span className="w-4 h-4 text-gray-400">-</span>;
		}
	};

	const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const year = Number.parseInt(e.target.value);
		if (!Number.isNaN(year) && year > 0) {
			setSelectedYear(year);
		}
	};

	const handleExport = async (format: "csv" | "xlsx" | "pdf") => {
		if (!filteredData || filteredData.length === 0) {
			return;
		}

		try {
			// Prepare request data
			const requestData = {
				year: selectedYear,
				salesperson_id: salespersonId,
				search_term: searchTerm || undefined,
			};

			// Map xlsx to excel for the API endpoint
			const apiFormat = format === "xlsx" ? "excel" : format;

			// Make API request based on format
			const response = await apiClient.post(`/collection-report/export/${apiFormat}`, requestData, {
				responseType: "blob",
			});

			// Create a download link
			const blob = new Blob([response.data]);
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;

			// Set filename based on format
			const date = new Date().toISOString().split("T")[0];
			const filename = `collection_report_${selectedYear}_${date}.${format === "xlsx" ? "xlsx" : format}`;
			link.download = filename;

			// Trigger download
			document.body.appendChild(link);
			link.click();

			// Clean up
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error("Export failed:", error);
			// You might want to show an error notification here
		}
	};

	const formatCurrency = (value: number) => {
		return new Intl.NumberFormat("en-PH", {
			style: "currency",
			currency: "PHP",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(value);
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-96">
				<div className="text-gray-500">Loading collection report...</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex items-center justify-center h-96">
				<div className="text-red-500">Failed to load collection report</div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			{/* Year Filter and Export */}
			<Card>
				<CardContent className="pt-6">
					<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
						<div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
							<div className="flex items-center gap-4">
								<div className="flex items-center gap-2">
									<IconCalendar className="w-5 h-5 text-gray-500" />
									<span className="text-sm font-medium text-gray-700">Year:</span>
								</div>
								<Input type="number" value={selectedYear} onChange={handleYearChange} className="w-24" min="2020" max="2030" />
							</div>
							<div className="flex items-center gap-2 w-full sm:w-auto">
								<div className="relative flex-1 sm:w-80">
									<IconSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
									<Input
										placeholder="Search by client, project, or salesperson..."
										value={searchTerm}
										onChange={(e) => setSearchTerm(e.target.value)}
										className="pl-9"
									/>
								</div>
							</div>
						</div>

						{filteredData.length > 0 && (
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button variant="outline">
										<IconFileExport className="w-4 h-4 mr-2" />
										Export Report
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align="end">
									<DropdownMenuItem onClick={() => handleExport("csv")}>
										<IconFileTypeCsv className="w-4 h-4 mr-2" />
										Export as CSV
									</DropdownMenuItem>
									<DropdownMenuItem onClick={() => handleExport("xlsx")}>
										<IconFileSpreadsheet className="w-4 h-4 mr-2" />
										Export as Excel
									</DropdownMenuItem>
									{/* <DropdownMenuItem onClick={() => handleExport("pdf")}>
										<IconFileTypePdf className="w-4 h-4 mr-2" />
										Export as PDF
									</DropdownMenuItem> */}
								</DropdownMenuContent>
							</DropdownMenu>
						)}
					</div>
				</CardContent>
			</Card>

			{/* Summary */}
			{(summaryData.totalCollected > 0 || summaryData.pendingCollection > 0) && (
				<Card>
					<CardHeader>
						<CardTitle>Summary for {selectedYear}</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							{/* Collected Summary */}
							<div className="bg-green-50 rounded-lg border">
								<button
									type="button"
									className="w-full text-center p-4 hover:bg-green-100 transition-colors rounded-lg"
									onClick={() => setShowCollectedBreakdown(!showCollectedBreakdown)}
								>
									<div className="flex items-center justify-center gap-2">
										<div>
											<div className="text-2xl font-bold text-green-900">{formatCurrency(summaryData.totalCollected)}</div>
											<div className="text-sm text-green-600">Total Collected</div>
										</div>
										{showCollectedBreakdown ? <IconChevronUp className="w-4 h-4 text-green-600" /> : <IconChevronDown className="w-4 h-4 text-green-600" />}
									</div>
								</button>

								{showCollectedBreakdown && (
									<div className="border-t border-green-200 p-4 bg-green-25">
										<div className="space-y-3">
											<div className="flex justify-between items-center">
												<span className="text-sm font-medium text-green-700">One-time Projects:</span>
											</div>
											{(summaryData.collectedBreakdown.oneTimeProjects.length > 0 || summaryData.collectedBreakdown.recurringProjects.length > 0) && (
												<div>
													<div className="text-xs text-green-600 mb-2">Projects breakdown:</div>
													<div className="max-h-48 overflow-y-auto space-y-1 pr-2">
														{summaryData.collectedBreakdown.oneTimeProjects.map((project, idx) => (
															<div key={`ot-${idx}`} className="text-xs flex justify-between">
																<span className="text-green-700">
																	{project.client} - {project.project}
																</span>
																<span className="text-green-800 font-medium">{formatCurrency(project.amount)}</span>
															</div>
														))}
													</div>
												</div>
											)}
											<div className="flex justify-between items-center">
												<div className="text-xs font-medium text-red-600 mb-2">Total Collected:</div>
												<span className="text-sm font-bold text-green-800">{formatCurrency(summaryData.collectedBreakdown.oneTime)}</span>
											</div>
											<div className="flex justify-between items-center mt-3 pt-3 border-t border-green-200">
												<span className="text-sm font-medium text-green-700 ">Recurring Projects:</span>
											</div>
											{(summaryData.collectedBreakdown.oneTimeProjects.length > 0 || summaryData.collectedBreakdown.recurringProjects.length > 0) && (
												<div className="">
													<div className="text-xs text-green-600 mb-2">Projects breakdown:</div>
													<div className="max-h-48 overflow-y-auto space-y-1 pr-2">
														{summaryData.collectedBreakdown.recurringProjects.map((project, idx) => (
															<div key={`rec-${idx}`} className="text-xs flex justify-between">
																<span className="text-green-700">
																	{project.client} - {project.project} ({project.months}mo)
																</span>
																<span className="text-green-800 font-medium">{formatCurrency(project.totalAmount)}</span>
															</div>
														))}
													</div>
												</div>
											)}
											<div className="flex justify-between items-center">
												<div className="text-xs font-medium text-red-600 mb-2">Total Collected:</div>
												<span className="text-sm font-bold text-green-800">{formatCurrency(summaryData.collectedBreakdown.recurring)}</span>
											</div>
										</div>
									</div>
								)}
							</div>

							{/* Pending Summary */}
							<div className="bg-red-50 rounded-lg border">
								<button
									type="button"
									className="w-full text-center p-4 hover:bg-red-100 transition-colors rounded-lg"
									onClick={() => setShowPendingBreakdown(!showPendingBreakdown)}
								>
									<div className="flex items-center justify-center gap-2">
										<div>
											<div className="text-2xl font-bold text-red-900">{formatCurrency(summaryData.pendingCollection)}</div>
											<div className="text-sm text-red-600">Pending Collection</div>
										</div>
										{showPendingBreakdown ? <IconChevronUp className="w-4 h-4 text-red-600" /> : <IconChevronDown className="w-4 h-4 text-red-600" />}
									</div>
								</button>

								{showPendingBreakdown && (
									<div className="border-t border-red-200 p-4 bg-red-25">
										<div className="space-y-3">
											<div className="flex justify-between items-center">
												<span className="text-sm font-medium text-red-700">One-time Projects:</span>
											</div>
											<div className="">
												{(summaryData.pendingBreakdown.oneTimeProjects.length > 0 || summaryData.pendingBreakdown.recurringProjects.length > 0) && (
													<div>
														<div className="text-xs font-medium text-red-600 mb-2">Projects breakdown:</div>
														<div className="max-h-48 overflow-y-auto space-y-1 pr-2">
															{summaryData.pendingBreakdown.oneTimeProjects.map((project, idx) => (
																<div key={`ot-${idx}`} className="text-xs flex justify-between">
																	<span className="text-red-700">
																		{project.client} - {project.project}
																	</span>
																	<span className="text-red-800 font-medium">{formatCurrency(project.amount)}</span>
																</div>
															))}
														</div>
													</div>
												)}
											</div>
											<div className="flex justify-between items-center">
												<div className="text-xs font-medium text-red-600 mb-2">Total Collected:</div>
												<span className="text-sm font-bold text-red-800">{formatCurrency(summaryData.pendingBreakdown.oneTime)}</span>
											</div>
											<div className="flex justify-between items-center mt-3 pt-3 border-t border-green-200">
												<span className="text-sm font-medium text-red-700">Recurring Projects:</span>
											</div>
											<div className="">
												{(summaryData.pendingBreakdown.oneTimeProjects.length > 0 || summaryData.pendingBreakdown.recurringProjects.length > 0) && (
													<div>
														<div className="text-xs font-medium text-red-600 mb-2">Projects breakdown:</div>
														<div className="max-h-48 overflow-y-auto space-y-1 pr-2">
															{summaryData.pendingBreakdown.recurringProjects.map((project, idx) => (
																<div key={`rec-${idx}`} className="text-xs flex justify-between">
																	<span className="text-red-700">
																		{project.client} - {project.project} ({project.months}mo)
																	</span>
																	<span className="text-red-800 font-medium">{formatCurrency(project.totalAmount)}</span>
																</div>
															))}
														</div>
													</div>
												)}
											</div>
											<div className="flex justify-between items-center">
												<div className="text-xs font-medium text-red-600 mb-2">Total Collected:</div>
												<span className="text-sm font-bold text-red-800">{formatCurrency(summaryData.pendingBreakdown.recurring)}</span>
											</div>
										</div>
									</div>
								)}
							</div>
						</div>
					</CardContent>
				</Card>
			)}

			{/* Collection Report Tables */}
			{filteredData.length === 0 ? (
				<Card>
					<CardContent className="pt-6">
						<div className="text-center py-8">
							<p className="text-gray-500">No contracts with invoices found for {selectedYear}</p>
						</div>
					</CardContent>
				</Card>
			) : (
				<div className="space-y-6">
					{/* Recurring Projects Table */}
					{filteredData.filter((contract) => contract.monthly_recurring_sales > 0).length > 0 && (
						<Card>
							<CardHeader>
								<CardTitle>Recurring Projects - {selectedYear}</CardTitle>
							</CardHeader>
							<CardContent>
								<div className="overflow-auto max-h-[500px] relative border rounded-lg">
									<table className="w-full text-sm">
										<thead className="sticky top-0 bg-blue-50 z-10">
											<tr className="border-b">
												<th className="text-left p-2 font-medium bg-blue-50">Client / Project</th>
												<th className="text-left p-2 font-medium bg-blue-50">Salesperson</th>
												<th className="text-right p-2 font-medium bg-blue-50">Contract Amount</th>
												<th className="text-right p-2 font-medium bg-blue-50">Monthly Recurring</th>
												{months.map((month) => (
													<th key={month} className="text-center p-2 font-medium w-12 bg-blue-50">
														{month}
													</th>
												))}
											</tr>
										</thead>
										<tbody>
											{filteredData
												.filter((contract) => contract.monthly_recurring_sales > 0)
												.map((contract) => {
													const monthlyStatus = getMonthlyStatus(contract);
													return (
														<tr key={contract.id} className="border-b hover:bg-blue-25">
															<td className="p-2">
																<div className="font-medium">{contract.client_name}</div>
																<div className="text-sm text-gray-600">{contract.project_name}</div>
															</td>
															<td className="p-2">
																<div>{contract.salesperson_name}</div>
															</td>
															<td className="p-2 text-right">
																<div className="font-medium">{formatCurrency(contract.contract_amount)}</div>
															</td>
															<td className="p-2 text-right">
																<div className="font-medium">{formatCurrency(contract.monthly_recurring_sales)}</div>
															</td>
															{months.map((month, index) => {
																const monthKey = (index + 1).toString().padStart(2, "0");
																const status = monthlyStatus[monthKey] || "not_applicable";
																return (
																	<td key={month} className="p-2 text-center">
																		{getStatusIcon(status)}
																	</td>
																);
															})}
														</tr>
													);
												})}
										</tbody>
										<tfoot className="sticky bottom-0 bg-blue-100 border-t-2 border-blue-300 z-10">
											<tr className="font-semibold">
												<td className="p-2">
													<div className="font-bold">TOTAL ({groupTotals.recurring.count} projects)</div>
													<div className="text-sm text-blue-700">Collected: {formatCurrency(groupTotals.recurring.collected)}</div>
													<div className="text-sm text-blue-700">Pending: {formatCurrency(groupTotals.recurring.pending)}</div>
												</td>
												<td className="p-2 bg-blue-100" />
												<td className="p-2 text-right bg-blue-100">
													{/* <div className="font-bold">{formatCurrency(groupTotals.recurring.contractAmount)}</div> */}
												</td>
												<td className="p-2 text-right bg-blue-100">
													{/* <div className="font-bold">{formatCurrency(groupTotals.recurring.monthlyRecurring)}</div> */}
												</td>
												{months.map((month) => (
													<td key={month} className="p-2 text-center bg-blue-100">
														<span className="w-4 h-4 text-blue-600">-</span>
													</td>
												))}
											</tr>
										</tfoot>
									</table>
								</div>
							</CardContent>
						</Card>
					)}

					{/* One-time Projects Table */}
					{filteredData.filter((contract) => contract.monthly_recurring_sales === 0).length > 0 && (
						<Card>
							<CardHeader>
								<CardTitle>One-time Projects - {selectedYear}</CardTitle>
							</CardHeader>
							<CardContent>
								<div className="overflow-auto max-h-[500px] relative border rounded-lg">
									<table className="w-full text-sm">
										<thead className="sticky top-0 bg-orange-50 z-10">
											<tr className="border-b">
												<th className="text-left p-2 font-medium bg-orange-50">Client / Project</th>
												<th className="text-left p-2 font-medium bg-orange-50">Salesperson</th>
												<th className="text-right p-2 font-medium bg-orange-50">Contract Amount</th>
												{months.map((month) => (
													<th key={month} className="text-center p-2 font-medium w-12 bg-orange-50">
														{month}
													</th>
												))}
											</tr>
										</thead>
										<tbody>
											{filteredData
												.filter((contract) => contract.monthly_recurring_sales === 0)
												.map((contract) => {
													const monthlyStatus = getMonthlyStatus(contract);
													return (
														<tr key={contract.id} className="border-b hover:bg-orange-25">
															<td className="p-2">
																<div className="font-medium">{contract.client_name}</div>
																<div className="text-sm text-gray-600">{contract.project_name}</div>
															</td>
															<td className="p-2">
																<div>{contract.salesperson_name}</div>
															</td>
															<td className="p-2 text-right">
																<div className="font-medium">{formatCurrency(contract.contract_amount)}</div>
															</td>
															{months.map((month, index) => {
																const monthKey = (index + 1).toString().padStart(2, "0");
																const status = monthlyStatus[monthKey] || "not_applicable";
																return (
																	<td key={month} className="p-2 text-center">
																		{getStatusIcon(status)}
																	</td>
																);
															})}
														</tr>
													);
												})}
										</tbody>
										<tfoot className="sticky bottom-0 bg-orange-100 border-t-2 border-orange-300 z-10">
											<tr className="font-semibold">
												<td className="p-2">
													<div className="font-bold">TOTAL ({groupTotals.oneTime.count} projects)</div>
													<div className="text-sm text-orange-700">Collected: {formatCurrency(groupTotals.oneTime.collected)}</div>
													<div className="text-sm text-orange-700">Pending: {formatCurrency(groupTotals.oneTime.pending)}</div>
												</td>
												<td className="p-2 bg-orange-100" />
												<td className="p-2 text-right bg-orange-100">
													{/* <div className="font-bold">{formatCurrency(groupTotals.oneTime.contractAmount)}</div> */}
												</td>
												{months.map((month) => (
													<td key={month} className="p-2 text-center bg-orange-100">
														<span className="w-4 h-4 text-orange-600">-</span>
													</td>
												))}
											</tr>
										</tfoot>
									</table>
								</div>
							</CardContent>
						</Card>
					)}
				</div>
			)}
		</div>
	);
};
